'use client';

import ListPageLoadingShell from '@/components/ListPageLoadingShell';
import ProductLoadingSkeleton from '@/components/ProductLoadingSkeleton';
import {
  beginInstantRouteTransition,
  clearInstantRouteTransition,
  getInstantRouteNavServerSnapshot,
  getInstantRouteNavSnapshot,
  subscribeInstantRouteNav,
} from '@/lib/instantRouteNavStore';
import { normalizeAppPathname } from '@/lib/instantRouteTransition';
import { usePathname } from 'next/navigation';
import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';

export { beginInstantRouteTransition, clearInstantRouteTransition };

function isModifiedClick(event: MouseEvent) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

/**
 * Listing/product skeleton below the header while soft-nav RSC is still pending.
 * Replaces segment loading.tsx so hard requests can still return HTTP 404 from notFound().
 */
export default function InstantRouteTransitionOverlay() {
  const pathname = usePathname();
  const nav = useSyncExternalStore(
    subscribeInstantRouteNav,
    getInstantRouteNavSnapshot,
    getInstantRouteNavServerSnapshot,
  );
  const [visible, setVisible] = useState(false);
  const [topOffset, setTopOffset] = useState(0);

  // Capture-phase only observes; never preventDefault — GTM / attribution click handlers stay intact.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || isModifiedClick(event)) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }

      beginInstantRouteTransition(href);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  useEffect(() => {
    const onAbort = () => clearInstantRouteTransition();
    window.addEventListener('popstate', onAbort);
    window.addEventListener('pageshow', onAbort);
    return () => {
      window.removeEventListener('popstate', onAbort);
      window.removeEventListener('pageshow', onAbort);
    };
  }, []);

  useEffect(() => {
    if (!nav) {
      setVisible(false);
      return;
    }
    const headerBottom = document.getElementById('hc-search')?.getBoundingClientRect().bottom ?? 0;
    setTopOffset(Math.max(0, headerBottom));
    setVisible(true);
  }, [nav]);

  useEffect(() => {
    if (!nav) return;

    const current = normalizeAppPathname(pathname || '/');

    // Next updates the address bar before the new RSC tree commits. Matching
    // pathname === target is NOT settled — StaleRouteGuard / page mount clears.
    const redirectedAway = current !== nav.fromPathname && current !== nav.pathname;
    if (redirectedAway) {
      clearInstantRouteTransition();
    }
  }, [pathname, nav]);

  if (!visible || !nav) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 overflow-y-auto bg-hzc_beige_200"
      style={{ top: topOffset }}
      aria-busy="true"
      aria-live="polite"
    >
      {nav.kind === 'product' ? (
        <div className="px-2 md:px-4 mt-4 mb-16 bg-hzc_beige_200">
          <ProductLoadingSkeleton />
        </div>
      ) : nav.kind === 'list' ? (
        <ListPageLoadingShell />
      ) : (
        <div className="min-h-screen bg-hzc_beige_200" />
      )}
    </div>
  );
}

/**
 * Keep the previous RSC tree in the document (so the next page can mount) but
 * hide it once the URL has already moved. Overlay + StaleRouteGuard cover paint.
 */
export function SoftNavChildrenGate({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const nav = useSyncExternalStore(
    subscribeInstantRouteNav,
    getInstantRouteNavSnapshot,
    getInstantRouteNavServerSnapshot,
  );
  const current = normalizeAppPathname(pathname || '/');
  const hideStale = !!nav && current === nav.pathname && current !== nav.fromPathname;

  return (
    <div
      aria-hidden={hideStale}
      className={hideStale ? 'invisible pointer-events-none' : undefined}
    >
      {children}
    </div>
  );
}
